import {useDndContext} from '@github-ui/drag-and-drop'
import {testIdProps} from '@github-ui/test-id-props'
import {
  AlertFillIcon,
  EyeClosedIcon,
  FilterIcon,
  GearIcon,
  KebabHorizontalIcon,
  MultiSelectIcon,
  RowsIcon,
  SortAscIcon,
  SortDescIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {memo, useCallback, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {
  type FieldGroupUIType,
  FieldHideName,
  FieldSortAsc,
  FieldSortDesc,
  TableHeaderMenuUI,
} from '../../../api/stats/contracts'
import type {SortDirection} from '../../../api/view/contracts'
import {useSliceBy} from '../../../features/slicing/hooks/use-slice-by'
import {not_typesafe_nonNullAssertion} from '../../../helpers/non-null-assertion'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {getHeaderCellPropsFromColumn} from '../../../hooks/tanstack-table/helpers/get-cell-styles'
import {
  getCanReorder,
  getCanResize,
  getColSpan,
  getHeaderColumn,
  getHeaderColumnModel,
  getIsResizing,
  getResizerProps,
  getSortInfo,
  headerIsGrouped,
} from '../../../hooks/tanstack-table/helpers/header-api'
import type {ColumnInstance, Header} from '../../../hooks/tanstack-table/helpers/types'
import {useGetDraggableHeaderProps} from '../../../hooks/tanstack-table/providers/columns/column-order-dnd-provider'
import useBodyClass from '../../../hooks/use-body-class'
import {useColumnWarning} from '../../../hooks/use-column-warning'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {useViews} from '../../../hooks/use-views'
import {useVisibleFields} from '../../../hooks/use-visible-fields'
import {
  isFilterableColumn,
  isGroupableColumn,
  isSliceableColumn,
  isSortableColumn,
} from '../../../models/column-capabilities'
import type {ColumnModel} from '../../../models/column-model'
import {useNavigate} from '../../../router'
import {useProjectRouteParams} from '../../../router/use-project-route-params'
import {PROJECT_SETTINGS_FIELD_ROUTE} from '../../../routes'
import {IssueFieldResources, Resources} from '../../../strings'
import {getColumnSortedAscendingDescription, getColumnSortedDescendingDescription} from '../../column-detail-helpers'
import {ColumnSortIcon} from '../../common/column-sort-icon'
import {useSearch} from '../../filter-bar/search-context'
import {MoveToFarLeftAction, MoveToFarRightAction, MoveToLeftAction, MoveToRightAction} from '../../move-to-actions'
import {RenameProjectFieldDialog} from '../../rename-project-field-dialog'
import {SlicerIcon} from '../../slice-by-menu'
import {describeSortRanking} from '../../sorted-by'
import {useGetVisibleRows} from '../hooks/use-visible-rows'
import {focusCell, focusSearchInput, useStableTableNavigation} from '../navigation'
import {useTableCellBulkSelectionActions} from '../table-cell-bulk-selection'
import type {TableDataType} from '../table-data-type'
import {columnHeaderTestId} from '../test-identifiers'
import {HeaderGridCell} from './header-grid-cell'
import {HeaderGridCellLayout} from './header-grid-cell-layout'
import styles from './table-header-cell.module.css'

function getSortDirectionLabel(canSort: boolean, descending: boolean | undefined) {
  if (!canSort) {
    return undefined
  } else if (descending === undefined) {
    return 'none'
  } else if (descending) {
    return 'descending'
  } else {
    return 'ascending'
  }
}

type TableHeaderCellProps = {
  header: Header<TableDataType>
  groupByColumn: (column: ColumnInstance<TableDataType>, ui: FieldGroupUIType) => void
  sliceByColumn: (column: ColumnModel) => void
  height: number
}

/**
 * An individual table column header. Only rendered if a columnModel is present.
 */
export const TableHeaderCell = memo(({groupByColumn, header, sliceByColumn, height}: TableHeaderCellProps) => {
  const columnModel = not_typesafe_nonNullAssertion(getHeaderColumnModel(header))
  const {currentView} = useViews()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const canSort = isSortableColumn(columnModel.dataType)
  const {isSorted, isSortedDesc, sortedIndex} = getSortInfo(header)
  const {appendFilter, focusFilterInput} = useSearch()
  const {postStats} = usePostStats()
  const {hideField, moveField, visibleFields, visibleFieldIndexMap} = useVisibleFields()
  const {hasWritePermissions} = ViewerPrivileges()
  const {memex_move_far_column_actions} = useEnabledFeatures()
  const {setColumnSort, sorts} = useSortedBy()
  const bulkSelectActions = useTableCellBulkSelectionActions()
  const getVisibleRows = useGetVisibleRows()
  const {sliceField} = useSliceBy()
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const warningKind = useColumnWarning(columnModel)
  const isGrouped = headerIsGrouped(header)
  const canResize = getCanResize(header)

  const isSliced = sliceField === columnModel
  const isTitleColumn = columnModel.dataType === MemexColumnDataType.Title
  const isSingleSelect = columnModel.dataType === MemexColumnDataType.SingleSelect
  const isSubIssuesProgress = columnModel.dataType === MemexColumnDataType.SubIssuesProgress

  const onHideColumn = useCallback(() => {
    if (!currentView) return

    hideField(currentView.number, columnModel)
    postStats({
      name: FieldHideName,
      ui: TableHeaderMenuUI,
      memexProjectColumnId: columnModel.id,
    })
  }, [columnModel, hideField, postStats, currentView])

  const sortByDirection = useCallback(
    (direction: SortDirection) => {
      if (!columnModel) return

      const isNewSortDesc = direction === 'desc'

      // Clear sort if target direction is same as current direction
      const resolvedDirection = isNewSortDesc === isSortedDesc ? null : direction

      setColumnSort(columnModel, resolvedDirection)

      // Close the cell options menu
      setMenuOpen(false)

      postStats({
        name: isNewSortDesc ? FieldSortDesc : FieldSortAsc,
        ui: TableHeaderMenuUI,
        context: columnModel.id,
      })
    },
    [columnModel, isSortedDesc, setColumnSort, setMenuOpen, postStats],
  )

  const GroupedLabel = useCallback(() => {
    if (!isGrouped) return null

    return (
      <div className={styles.Box} {...testIdProps(`grouped-label-${columnModel.name}`)}>
        <Octicon icon={RowsIcon} />
      </div>
    )
  }, [columnModel.name, isGrouped])

  const SortedLabel = useCallback(() => {
    const showSortRank = sorts.length > 1
    const sortRankDescription = showSortRank ? `${describeSortRanking(sortedIndex)} ` : ''

    if (!isSorted) return null

    return (
      <Button
        variant="invisible"
        size="small"
        aria-label={`Change ${sortRankDescription}sort direction to ${isSortedDesc ? 'ascending' : 'descending'}`}
        onClick={() => sortByDirection(isSortedDesc ? 'asc' : 'desc')}
        className={styles.Button}
        {...testIdProps(`sorted-label-${columnModel.id}`)}
      >
        <ColumnSortIcon direction={isSortedDesc ? 'desc' : 'asc'} index={showSortRank ? sortedIndex : undefined} />
      </Button>
    )
  }, [isSorted, isSortedDesc, columnModel.id, sorts.length, sortedIndex, sortByDirection])

  const SlicedLabel = useCallback(() => {
    if (!isSliced) return null

    return (
      <div className={styles.Box} {...testIdProps(`sliced-label-${columnModel.name}`)}>
        <Octicon icon={SlicerIcon} />
      </div>
    )
  }, [columnModel.name, isSliced])

  const ascendingDescription = getColumnSortedAscendingDescription(columnModel.dataType)
  const descendingDescription = getColumnSortedDescendingDescription(columnModel.dataType)

  const sortMenuOptions = useMemo(() => {
    if (!canSort) return []

    const isSortedAscending = isSorted && !isSortedDesc
    const isSortedDescending = isSorted && isSortedDesc

    return [
      <ActionList.Item
        key={`sort-ascending-${columnModel.id}`}
        onSelect={() => sortByDirection('asc')}
        className={isSortedAscending ? styles.sorted : styles.unsorted}
        aria-describedby={`sort-description-asc-${columnModel.id}`}
        {...testIdProps(`sort-toggle-asc-${columnModel.id}`)}
      >
        <ActionList.LeadingVisual>
          <SortAscIcon />
        </ActionList.LeadingVisual>

        <span>
          {isSortedAscending
            ? Resources.tableHeaderContextMenu.sortAscendingActive
            : Resources.tableHeaderContextMenu.sortAscending}
        </span>

        <ActionList.TrailingVisual>{isSortedAscending && <XIcon />}</ActionList.TrailingVisual>
      </ActionList.Item>,

      <span
        key={`sort-description-asc-${columnModel.id}`}
        id={`sort-description-asc-${columnModel.id}`}
        className="sr-only"
      >
        {ascendingDescription}
      </span>,

      <ActionList.Item
        key={`sort-descending-${columnModel.id}`}
        onSelect={() => sortByDirection('desc')}
        className={isSortedDescending ? styles.sorted : styles.unsorted}
        aria-describedby={`sort-description-desc-${columnModel.id}`}
        {...testIdProps(`sort-toggle-desc-${columnModel.id}`)}
      >
        <ActionList.LeadingVisual>
          <SortDescIcon />
        </ActionList.LeadingVisual>

        <span>
          {isSortedDescending
            ? Resources.tableHeaderContextMenu.sortDescendingActive
            : Resources.tableHeaderContextMenu.sortDescending}
        </span>

        <ActionList.TrailingVisual>{isSortedDescending && <XIcon />}</ActionList.TrailingVisual>
      </ActionList.Item>,

      <span
        key={`sort-description-desc-${columnModel.id}`}
        id={`sort-description-desc-${columnModel.id}`}
        className="sr-only"
      >
        {descendingDescription}
      </span>,
    ]
  }, [canSort, isSorted, isSortedDesc, columnModel.id, ascendingDescription, descendingDescription, sortByDirection])

  const navigate = useNavigate()
  const projectRouteParams = useProjectRouteParams()
  const {navigationDispatch} = useStableTableNavigation()

  const onCreateFilter = useCallback(
    (filterKeyword: string, e: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
      appendFilter(filterKeyword)
      focusFilterInput()
      navigationDispatch(focusSearchInput())

      // Stop this event from closing the search suggestions (as an outside click).
      e.stopPropagation()
    },
    [appendFilter, focusFilterInput, navigationDispatch],
  )

  const moveToLeft = useCallback(() => {
    if (!currentView) {
      return
    }

    const currentIndex = visibleFields.indexOf(columnModel)

    moveField(currentView.number, columnModel, currentIndex - 1)
  }, [columnModel, currentView, moveField, visibleFields])

  const moveToRight = useCallback(() => {
    if (!currentView) {
      return
    }

    const currentIndex = visibleFields.indexOf(columnModel)

    moveField(currentView.number, columnModel, currentIndex + 1)
  }, [columnModel, currentView, moveField, visibleFields])

  const moveToFarLeft = useCallback(() => {
    if (!currentView) {
      return
    }

    const currentIndex = visibleFields.indexOf(columnModel)

    if (currentIndex <= 0) {
      return
    }

    moveField(currentView.number, columnModel, 0)
  }, [columnModel, currentView, moveField, visibleFields])

  const moveToFarRight = useCallback(() => {
    if (!currentView) {
      return
    }

    const currentIndex = visibleFields.indexOf(columnModel)

    if (currentIndex === -1 || currentIndex >= visibleFields.length - 1) {
      return
    }

    moveField(currentView.number, columnModel, visibleFields.length - 1)
  }, [columnModel, currentView, moveField, visibleFields])

  const createFilterOptions = useMemo(() => {
    const defaultOptions = [
      <ActionList.Item
        key={`filter-by-values-${columnModel.id}`}
        onSelect={event => {
          // eslint-disable-next-line @eslint-react/dom-no-flush-sync
          flushSync(() => {
            setMenuOpen(false)
          })
          onCreateFilter(columnModel.name, event)
        }}
        disabled={!isFilterableColumn(columnModel.dataType)}
        {...testIdProps('create-filter-trigger')}
      >
        <ActionList.LeadingVisual>
          <FilterIcon />
        </ActionList.LeadingVisual>
        {Resources.tableHeaderContextMenu.filterValues}
      </ActionList.Item>,
    ]
    if (isTitleColumn) {
      defaultOptions.push(
        <ActionList.Item
          key={`filter-by-is-${columnModel.id}`}
          onSelect={event => {
            onCreateFilter('is', event)
            setMenuOpen(false)
          }}
          {...testIdProps('create-is-filter-trigger')}
        >
          <ActionList.LeadingVisual>
            <FilterIcon />
          </ActionList.LeadingVisual>
          {Resources.tableHeaderContextMenu.filterByType}
        </ActionList.Item>,
      )
    }
    return defaultOptions
  }, [columnModel.id, columnModel.dataType, columnModel.name, isTitleColumn, onCreateFilter, setMenuOpen])

  const onGroupByColumn = useCallback(() => {
    groupByColumn(getHeaderColumn(header), TableHeaderMenuUI)
    setMenuOpen(false)
  }, [groupByColumn, header, setMenuOpen])

  const canGroupBy = isGroupableColumn(columnModel.dataType)
  const groupByOptions = useMemo(() => {
    if (!canGroupBy) return []

    return [
      <ActionList.Item
        key={`group-by-${columnModel.id}`}
        onSelect={onGroupByColumn}
        className={isGrouped ? styles.sorted : styles.unsorted}
        {...testIdProps(`group-by-trigger`)}
      >
        <ActionList.LeadingVisual>
          <RowsIcon />
        </ActionList.LeadingVisual>
        {isGrouped ? Resources.tableHeaderContextMenu.groupByActive : Resources.tableHeaderContextMenu.groupByValues}
        <ActionList.TrailingVisual>{isGrouped && <XIcon />}</ActionList.TrailingVisual>
      </ActionList.Item>,
    ]
  }, [canGroupBy, isGrouped, columnModel.id, onGroupByColumn])

  const onSliceByColumn = useCallback(() => {
    sliceByColumn(columnModel)
    setMenuOpen(false)
  }, [columnModel, setMenuOpen, sliceByColumn])

  const sliceByOptions = useMemo(() => {
    if (!isSliceableColumn(columnModel.dataType)) return []

    return [
      <ActionList.Item
        key={`slice-by-${columnModel.id}`}
        onSelect={onSliceByColumn}
        className={isSliced ? styles.sorted : styles.unsorted}
        disabled={!isSliceableColumn(columnModel.dataType)}
        {...testIdProps(`slice-by-trigger`)}
      >
        <ActionList.LeadingVisual>
          <SlicerIcon />
        </ActionList.LeadingVisual>
        {Resources.tableHeaderContextMenu.sliceByValues}
        <ActionList.TrailingVisual>{isSliced && <XIcon />}</ActionList.TrailingVisual>
      </ActionList.Item>,
    ]
  }, [columnModel, onSliceByColumn, isSliced])

  const resizerProps = getResizerProps(header)

  const onMouseDown: React.MouseEventHandler<HTMLElement> = useCallback(
    e => {
      e.stopPropagation()
      resizerProps.onMouseDown?.(e)
    },
    [resizerProps],
  )

  const ColumnResizeDragger = useCallback(() => {
    if (!canResize || !hasWritePermissions) return null

    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          {...resizerProps}
          onMouseDown={onMouseDown}
          className={styles.Box_1}
          {...testIdProps(`${columnModel.name}-column-resizer`)}
        />
      </>
    )
  }, [onMouseDown, canResize, resizerProps, columnModel, hasWritePermissions])

  const dragProps = useGetDraggableHeaderProps(header.id, getCanReorder(header))

  const dndContext = useDndContext()
  const colSpan = getColSpan(header)
  const headerProps = useMemo(
    () => ({
      className: `${!hasWritePermissions ? 'readonly' : dndContext.active ? '' : 'hoverable'}`,
      ...getHeaderCellPropsFromColumn({
        columnId: header.id,
        colSpan,
      }),
    }),
    [hasWritePermissions, dndContext.active, header.id, colSpan],
  )

  useBodyClass('is-resizing', getIsResizing(header))

  const selectColumn = useCallback(() => {
    const firstRow = getVisibleRows()[0]
    if (!firstRow || !bulkSelectActions) return

    // setTimeout is a hack to get around the return-focus behavior of the action menu by focusing the cell after the menu closes
    setTimeout(() => {
      // Technically only the focus dispatch needs to be async, but it feels faster if the selection & focus happen at the same time
      bulkSelectActions.selectColumn(header.id)
      navigationDispatch(focusCell(firstRow.id, header.id))
    })
  }, [bulkSelectActions, navigationDispatch, header.id, getVisibleRows])

  const positionActions = useMemo(() => {
    if (hasWritePermissions) {
      const isFirstColumn = visibleFieldIndexMap.get(columnModel.id) === 0
      const isLastColumn = visibleFieldIndexMap.get(columnModel.id) === visibleFields.length - 1

      return [
        <MoveToLeftAction
          key="moveToLeft"
          disabled={isFirstColumn}
          isLeftmostColumn={isFirstColumn}
          onSelect={moveToLeft}
        />,
        <MoveToRightAction
          key="moveToRight"
          disabled={isLastColumn}
          isRightmostColumn={isLastColumn}
          onSelect={moveToRight}
        />,
        ...(memex_move_far_column_actions
          ? [
              <MoveToFarLeftAction
                key="moveToFarLeft"
                disabled={isFirstColumn}
                isLeftmostColumn={isFirstColumn}
                onSelect={moveToFarLeft}
              />,
              <MoveToFarRightAction
                key="moveToFarRight"
                disabled={isLastColumn}
                isRightmostColumn={isLastColumn}
                onSelect={moveToFarRight}
              />,
            ]
          : []),
      ]
    }
    return []
  }, [
    hasWritePermissions,
    visibleFieldIndexMap,
    columnModel.id,
    visibleFields.length,
    moveToLeft,
    moveToFarLeft,
    moveToRight,
    moveToFarRight,
    memex_move_far_column_actions,
  ])

  return useMemo(() => {
    const filterOperations = createFilterOptions
    const groupOperations = [...groupByOptions, ...sliceByOptions]
    const canBulkSelectColumn = bulkSelectActions !== null

    const hasWarning = warningKind === 'column-name-collides-with-issue-field'

    return (
      <>
        <HeaderGridCell
          {...headerProps}
          {...testIdProps(columnHeaderTestId(header.id))}
          aria-sort={getSortDirectionLabel(canSort, isSortedDesc)}
        >
          <HeaderGridCellLayout
            style={{
              boxShadow: `inset 0 -2px 0 ${
                SortedLabel() || GroupedLabel() || SlicedLabel()
                  ? 'var(--bgColor-neutral-emphasis)'
                  : 'var(--borderColor-default)'
              }, inset 0 1px 0 var(--borderColor-default)`,
              height,
            }}
            ref={dragProps.setNodeRef}
            className={styles.HeaderGridCellLayout}
            {...dragProps.listeners}
          >
            <div className={styles.textContainer}>
              <span className={styles.Text} {...testIdProps(`${columnModel.name}-column-header-name`)}>
                {columnModel.name}
              </span>
              {hasWarning && (
                <IconButton
                  icon={AlertFillIcon}
                  variant="invisible"
                  size="small"
                  tooltipDirection="n"
                  aria-label={IssueFieldResources.fieldNameCollisionTooltip}
                  className={styles.warningIcon}
                  onClick={() => setIsRenameDialogOpen(true)}
                  {...testIdProps(`column-warning-icon-${columnModel.name}`)}
                />
              )}
            </div>
            <div className={styles.Box_2}>
              <GroupedLabel />
              <SlicedLabel />
              <SortedLabel />
              <ActionMenu open={menuOpen} onOpenChange={setMenuOpen} anchorRef={anchorRef}>
                <ActionMenu.Anchor>
                  <IconButton
                    ref={anchorRef}
                    icon={KebabHorizontalIcon}
                    variant="invisible"
                    size="small"
                    aria-label={`${columnModel.name} column options`}
                    className={styles.IconButton}
                    {...testIdProps(`${columnModel.name}-column-menu-trigger`)}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay {...testIdProps(`${columnModel.name}-column-menu`)} initialFocusRef={anchorRef}>
                  <ActionList>
                    {canBulkSelectColumn && (
                      <ActionList.Item onSelect={selectColumn}>
                        <ActionList.LeadingVisual>
                          <MultiSelectIcon />
                        </ActionList.LeadingVisual>
                        {Resources.tableHeaderContextMenu.selectColumn}
                      </ActionList.Item>
                    )}

                    {sortMenuOptions.length > 0 && <ActionMenu.Divider />}
                    {sortMenuOptions}

                    {filterOperations.length > 0 && <ActionMenu.Divider />}
                    {filterOperations}

                    {groupOperations.length > 0 && <ActionMenu.Divider />}
                    {groupOperations}

                    {!isTitleColumn && (
                      <>
                        <ActionMenu.Divider />
                        <ActionList.Item onSelect={onHideColumn} disabled={isSorted}>
                          <ActionList.LeadingVisual>
                            <EyeClosedIcon />
                          </ActionList.LeadingVisual>
                          {Resources.tableHeaderContextMenu.hideField}
                        </ActionList.Item>
                      </>
                    )}

                    {hasWritePermissions && (columnModel.userDefined || isSingleSelect || isSubIssuesProgress) && (
                      <>
                        <ActionList.Divider />
                        <ActionList.Item
                          onSelect={() => {
                            navigate({
                              pathname: PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
                                ...projectRouteParams,
                                fieldId: columnModel.id,
                              }),
                            })
                          }}
                          disabled={!hasWritePermissions}
                        >
                          <ActionList.LeadingVisual>
                            <GearIcon />
                          </ActionList.LeadingVisual>
                          {Resources.tableHeaderContextMenu.fieldSettings}
                        </ActionList.Item>
                      </>
                    )}

                    {positionActions.length > 0 && (
                      <>
                        <ActionList.Divider />
                        <ActionList.Group>{positionActions}</ActionList.Group>
                      </>
                    )}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </div>
          </HeaderGridCellLayout>
          <ColumnResizeDragger />
        </HeaderGridCell>
        {isRenameDialogOpen && (
          <RenameProjectFieldDialog
            column={columnModel}
            onCancel={() => {
              setIsRenameDialogOpen(false)
            }}
            onConfirm={() => {
              setIsRenameDialogOpen(false)
            }}
          />
        )}
      </>
    )
  }, [
    createFilterOptions,
    groupByOptions,
    sliceByOptions,
    bulkSelectActions,
    warningKind,
    headerProps,
    header.id,
    canSort,
    isSortedDesc,
    SortedLabel,
    GroupedLabel,
    SlicedLabel,
    height,
    dragProps.setNodeRef,
    dragProps.listeners,
    columnModel,
    menuOpen,
    selectColumn,
    sortMenuOptions,
    isTitleColumn,
    onHideColumn,
    isSorted,
    hasWritePermissions,
    isSingleSelect,
    isSubIssuesProgress,
    positionActions,
    ColumnResizeDragger,
    isRenameDialogOpen,
    navigate,
    projectRouteParams,
  ])
})

TableHeaderCell.displayName = 'TableHeaderCell'
